import { colord } from "colord";
import { allColors, allMainColors } from "@foodpilot/foods";
import { SsqSsqHeading, SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export type OlapHeadingColorContext = {
    ssqVersion?: SsqVersion;
};

const CONFIGURABLE_PALETTE_SIZE = 15;

const paletteColorFromKey = (colorKey: string): string | null => {
    const paletteKey = Number(colorKey);
    if (!Number.isInteger(paletteKey) || paletteKey < 1 || paletteKey > CONFIGURABLE_PALETTE_SIZE) return null;

    return allColors[paletteKey as keyof typeof allColors].main;
};

const fallbackColor = (index: number): string => allMainColors[Math.abs(index) % CONFIGURABLE_PALETTE_SIZE];

const resolveFromLevel = (pivotId: number, pivots: SsqSsqHeading[]): string | null => {
    const index = pivots.findIndex((pivot) => pivot.id === pivotId);

    if (index !== -1) {
        const configuredColor = pivots[index].ssqHeading.color;

        return (configuredColor ? paletteColorFromKey(configuredColor) : null) ?? fallbackColor(index);
    }

    for (const pivot of pivots) {
        const descendantColor = resolveFromLevel(pivotId, pivot.children ?? []);

        if (descendantColor) return descendantColor;
    }

    return null;
};

const SHADE_LIGHTNESS_LIGHTEST = 0.78;
const SHADE_LIGHTNESS_DARKEST = 0.3;
const SHADE_LIGHTNESS_MIDPOINT = (SHADE_LIGHTNESS_LIGHTEST + SHADE_LIGHTNESS_DARKEST) / 2;
const SINGLE_SHADE_DARK = 0.38;
const SINGLE_SHADE_LIGHT = 0.72;
const SHADE_MIN_SATURATION = 0.35;

const HEADING_DIMENSIONS = ["headings", "subheadings"];

export class OlapHeadingColorService {
    public static resolve(pivotId: string | number, context?: OlapHeadingColorContext): string | null {
        return resolveFromLevel(Number(pivotId), context?.ssqVersion?.parentSsqSsqHeadings ?? []);
    }

    public static resolveMany(
        dimensionValues: Array<{ dimension: string; id: string | number }>,
        context?: OlapHeadingColorContext,
    ): string[] {
        let unresolvedSequence = context?.ssqVersion?.parentSsqSsqHeadings?.length ?? 0;

        return dimensionValues.map(({ dimension, id }) => {
            const headingColor = HEADING_DIMENSIONS.includes(dimension) ? this.resolve(id, context) : null;

            return headingColor ?? fallbackColor(unresolvedSequence++);
        });
    }

    public static deriveShades(parentColor: string, count: number): string[] {
        const { h, s, l } = colord(parentColor).toHsl();
        const saturation = Math.max(s, SHADE_MIN_SATURATION * 100);

        if (count === 1) {
            const onlyChildLightness = l / 100 > SHADE_LIGHTNESS_MIDPOINT ? SINGLE_SHADE_DARK : SINGLE_SHADE_LIGHT;

            return [colord({ h, s: saturation, l: onlyChildLightness * 100 }).toHex()];
        }

        const step = (SHADE_LIGHTNESS_LIGHTEST - SHADE_LIGHTNESS_DARKEST) / (count - 1);

        return Array.from({ length: count }, (_, index) =>
            colord({ h, s: saturation, l: (SHADE_LIGHTNESS_LIGHTEST - index * step) * 100 }).toHex(),
        );
    }
}
